import { Box, Text as ChakraText, VStack } from '@chakra-ui/react';
import type { LhcMutationGroupSummary } from '@diamond/shared/types';
import { replaceIfEmpty } from '@diamond/shared/utils';
import { formatCurrencyIdr } from '@diamond/shared/utils';

import type { LHCDetailInvoiceRowData } from '../../../types';

const SUMMARY_FIELDS: Array<{
  key: string;
  label: string;
  value: (rowData: LHCDetailInvoiceRowData) => string;
  isTotal?: boolean;
}> = [
  {
    key: 'customerName',
    label: 'Nama Customer',
    value: (rowData) => rowData.customerName,
  },
  {
    key: 'outletName',
    label: 'Nama Outlet',
    value: (rowData) => rowData.outletName,
  },
  { key: 'billTo', label: 'Billing ID', value: (rowData) => rowData.billTo },
  {
    key: 'poValue',
    label: 'Nilai Purchase Order',
    value: (rowData) => rowData.poValue,
  },
  { key: 'poNumber', label: 'No.PO', value: (rowData) => rowData.poNumber },
  {
    key: 'deliveryOrderNo',
    label: 'Surat Jalan',
    value: (rowData) => rowData.deliveryOrderNo,
  },
  {
    key: 'invoiceNo',
    label: 'Nomor Faktur',
    value: (rowData) => rowData.invoiceNo,
  },
  {
    key: 'invoiceDate',
    label: 'Tanggal Faktur',
    value: (rowData) => rowData.invoiceDate,
  },
  {
    key: 'invoiceValue',
    label: 'Total Faktur',
    value: (rowData) =>
      rowData.groupTotalInvoiceAmount &&
      rowData.groupTotalInvoiceAmount !== '-'
        ? rowData.groupTotalInvoiceAmount
        : rowData.invoiceValue,
    isTotal: true,
  },
];

interface MutationContextSummaryProps {
  rowData: LHCDetailInvoiceRowData | null;
  groupSummary?: LhcMutationGroupSummary | null;
  showUsedMutations?: boolean;
}

const SUBHEADER_TEXT_PROPS = {
  fontWeight: 400,
  fontSize: '10px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: '#8E8E93',
} as const;

const TITLE_TEXT_PROPS = {
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: '#373F41',
  whiteSpace: 'nowrap',
} as const;

const TOTAL_FAKTUR_TEXT_PROPS = {
  ...TITLE_TEXT_PROPS,
  color: '#007AFF',
} as const;

export function MutationContextSummary({
  rowData,
  groupSummary = null,
  showUsedMutations = false,
}: MutationContextSummaryProps) {
  if (!rowData) {
    return null;
  }

  const hasUsedMutations =
    showUsedMutations && rowData.bankMutations?.length > 0;

  return (
    <Box mb={6}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(5, auto)"
        columnGap={6}
        rowGap={4}
        width="100%"
      >
        {SUMMARY_FIELDS.map((field) => (
          <Box key={field.key}>
            <ChakraText {...SUBHEADER_TEXT_PROPS} mb={1}>
              {field.label}
            </ChakraText>
            <ChakraText
              {...(field.isTotal ? TOTAL_FAKTUR_TEXT_PROPS : TITLE_TEXT_PROPS)}
            >
              {replaceIfEmpty(field.value(rowData))}
            </ChakraText>
          </Box>
        ))}

        {groupSummary ? (
          <>
            <Box>
              <ChakraText {...SUBHEADER_TEXT_PROPS} mb={1}>
                Total Alokasi PO
              </ChakraText>
              <ChakraText {...TITLE_TEXT_PROPS}>
                {formatCurrencyIdr(groupSummary.total_allocated_amount)}
              </ChakraText>
            </Box>
            <Box>
              <ChakraText {...SUBHEADER_TEXT_PROPS} mb={1}>
                Sisa PO
              </ChakraText>
              <ChakraText {...TITLE_TEXT_PROPS}>
                {formatCurrencyIdr(groupSummary.outstanding_amount)}
              </ChakraText>
            </Box>
          </>
        ) : null}
        {hasUsedMutations ? (
          <Box>
            <ChakraText {...SUBHEADER_TEXT_PROPS} mb={1}>
              Mutasi Terpakai
            </ChakraText>
            <VStack align="start" spacing={1}>
              {rowData.bankMutations.map((m, idx) => (
                <ChakraText
                  key={m.bankMutationId}
                  {...TITLE_TEXT_PROPS}
                  color="#0000A3"
                >
                  <Box as="span" color="#373F41">
                    {idx + 1}
                  </Box>
                  .&nbsp;&nbsp;{m.bankMutationNo}
                </ChakraText>
              ))}
            </VStack>
          </Box>
        ) : (
          <Box />
        )}
      </Box>
    </Box>
  );
}
